import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import LazyImage from "@/components/site/LazyImage";
import { Reveal, WordsReveal } from "@/components/site/Reveal";

const NARRATIVE = [
  "Vruksha Valley was born from a deep love for the misty mountains, lush forests, coffee estates, and timeless beauty of the Western Ghats. Our vision was never simply to create a place to stay. It was to create a sanctuary where you can slow down, breathe deeper, and reconnect with nature.",
  "Set amidst the enchanting landscapes of Malnad, Vruksha Valley is surrounded by the region's natural and cultural treasures. The serene Bhadra River, the spiritual presence of the Kalaseshwara Temple, and the verdant Western Ghats together create an experience that is uniquely rooted in this land.",
  "As custodians of this beautiful setting, we are committed to preserving the warmth and authenticity of Malnad hospitality while bringing it together with contemporary eco-luxury.",
];

const DETAIL_LINES = [
  "It is waking up to clouds drifting across the valley through the glass walls of your cottage.",
  "It is the fragrance of coffee from the surrounding estate.",
  "It is a leisurely meal prepared with traditional Malnad flavours.",
  "It is an evening spent by the bonfire as the forest grows quiet around you.",
];

const MILESTONES = [
  {
    tag: "01 · Design",
    title: "Pioneering Eco-Luxury Architecture",
    text:
      "We reimagined the traditional mountain retreat with distinctive A-frame timber and glass cottages, designed to bring the outdoors in. Floor-to-ceiling views allow guests to wake up to the valley, mist, and ever-changing landscapes of the Western Ghats.",
    image:
      "https://customer-assets-m6fa6gv7.emergentagent.net/job_5dd34c9f-6dff-45ce-a6d1-857af95232c8/artifacts/4whvboid_image%20no1.png",
  },
  {
    tag: "02 · Water",
    title: "An Iconic Aquatic Experience",
    text:
      "Our twin pools are designed for both tranquillity and unforgettable moments. The signature infinity pool appears to merge with the valley, creating a spectacular setting for relaxation, sunsets, and photographs.",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/vvvlqwg6_swimming%2001.jfif",
  },
  {
    tag: "03 · Kitchen",
    title: "Preserving Authentic Malnad Flavours",
    text:
      "At the heart of the Vruksha Valley experience is our Malnad Kitchen. Traditional recipes, locally inspired ingredients, and slow-cooked preparations bring the authentic flavours of the region to your table — offering a taste of Malnad that goes beyond the ordinary.",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/62l4t0k3_food%20new%2001.jfif",
  },
  {
    tag: "04 · Heritage",
    title: "Connected to Nature & Heritage",
    text:
      "Vruksha Valley offers more than a retreat; it offers a chance to experience the character of Malnad. From the tranquil Bhadra River to the revered Kalaseshwara Temple, the surrounding landscape carries stories, traditions, and experiences waiting to be discovered.",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/tdi6q7ab_kalsa%20temple.jfif",
  },
  {
    tag: "05 · Explore",
    title: "The Ultimate Explorer's Base",
    text:
      "For those who seek adventure, Vruksha Valley is perfectly placed to explore the best of the region. Soormane Falls, Kudremukh, Netravati Peak, Horanadu, the Bhadra River, and Kalaseshwara Temple are all within reach, making the valley an ideal base for discovering the natural and cultural treasures of Malnad.",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/yxr9vy3c_soormane%20fals.jfif",
  },
  {
    tag: "06 · Moments",
    title: "Creating Unforgettable Moments",
    text:
      "From intimate escapes for couples and refreshing weekends with family to group celebrations beneath the stars, Vruksha Valley is a place where moments become memories. As evening falls, gather around the estate bonfire, listen to the sounds of nature, look up at the night sky, and let Malnad do what it does best — slow you down.",
    image:
      "https://customer-assets-39nsmqrw.emergentagent.net/job_ghats-luxury/artifacts/7g4dnltp_camp.jfif",
  },
];

// Shared Legacy content used on the /legacy page and on the Home page (below Guest Stories)
export default function LegacyContent({ testId = "legacy-content" }) {
  return (
    <div data-testid={testId}>
      {/* THE HEART */}
      <section className="bg-ivory py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <Reveal>
            <p className="mb-5 text-xs uppercase tracking-widest-2 text-brown md:text-sm">
              The Heart Behind Vruksha Valley
            </p>
          </Reveal>
          <h2 className="font-display text-3xl font-bold leading-[1.12] tracking-tight text-forest sm:text-4xl md:text-5xl lg:text-6xl">
            <WordsReveal text="Some places are built. Others are born from a feeling." />
          </h2>

          <div className="mt-10 space-y-6 md:mt-12">
            {NARRATIVE.map((p, i) => (
              <Reveal key={i} delay={0.15 + i * 0.1}>
                <p className="text-base leading-relaxed text-charcoal/75 md:text-lg">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LUXURY DETAILS — dark editorial band */}
      <section className="relative overflow-hidden bg-forest py-16 text-ivory md:py-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(235,200,117,0.9) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.6) 0%, transparent 50%)",
          }}
        />
        <div className="relative mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <p className="mb-5 flex items-center gap-2 text-xs uppercase tracking-widest-2 text-gold md:text-sm">
              <Sparkles className="h-3.5 w-3.5" /> Here, luxury is found in the details
            </p>
          </Reveal>

          <h2 className="max-w-3xl font-display text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl md:text-5xl">
            <WordsReveal text="Luxury, quietly. Woven into every hour of your stay." />
          </h2>

          <ul className="mt-12 grid gap-5 md:mt-14 md:grid-cols-2 md:gap-6">
            {DETAIL_LINES.map((line, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <li className="group flex gap-4 rounded-2xl border border-ivory/12 bg-ivory/[0.03] p-5 backdrop-blur-sm transition-colors duration-300 hover:border-gold/40 md:p-6">
                  <span
                    aria-hidden
                    className="mt-1 h-6 w-[2px] shrink-0 rounded-full"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(235,200,117,0) 0%, #EBC875 50%, rgba(235,200,117,0) 100%)",
                    }}
                  />
                  <p className="text-base leading-relaxed text-ivory/85 md:text-lg">{line}</p>
                </li>
              </Reveal>
            ))}
          </ul>

          <Reveal delay={0.35}>
            <p className="mt-12 max-w-3xl text-base leading-relaxed text-ivory/70 md:mt-14 md:text-lg">
              Every timber frame, every cottage, every view, and every experience at Vruksha Valley has been thoughtfully created with one purpose — to help you disconnect from the ordinary and reconnect with the wild.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MILESTONES */}
      <section className="bg-ivory py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <p className="mb-5 text-xs uppercase tracking-widest-2 text-brown md:text-sm">
                Milestones That Define Us
              </p>
            </Reveal>
            <h2 className="font-display text-3xl font-bold leading-[1.1] tracking-tight text-forest sm:text-4xl md:text-5xl lg:text-6xl">
              <WordsReveal text="From a dream in the Western Ghats to a destination for unforgettable escapes." />
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-7 text-base leading-relaxed text-charcoal/70 md:text-lg">
                What began as a vision for a peaceful nature retreat has grown into a destination where nature, architecture, hospitality, and Malnad heritage come together.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 md:mt-20 md:gap-8 lg:grid-cols-3">
            {MILESTONES.map((m, i) => (
              <motion.article
                key={m.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group flex flex-col overflow-hidden rounded-3xl border border-charcoal/8 bg-card shadow-[0_10px_40px_rgba(26,54,38,0.06)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_28px_60px_rgba(26,54,38,0.14)]"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <LazyImage
                    src={m.image}
                    alt={m.title}
                    fit="cover"
                    className="h-full w-full transition-transform duration-[1200ms] group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-forest/45 via-transparent to-transparent" />
                  <span
                    className="absolute left-4 top-4 rounded-full border border-ivory/50 bg-forest/40 px-3 py-1 text-[10px] uppercase tracking-widest-2 text-ivory backdrop-blur-md md:text-xs"
                    style={{ letterSpacing: "0.24em" }}
                  >
                    {m.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6 md:p-8">
                  <h3 className="font-display text-xl font-bold leading-[1.2] text-forest sm:text-2xl md:text-[26px]">
                    {m.title}
                  </h3>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-charcoal/70 md:text-base">
                    {m.text}
                  </p>
                  <span
                    aria-hidden
                    className="mt-6 block h-[2px] w-14"
                    style={{
                      background:
                        "linear-gradient(90deg, #B7873B 0%, #EBC875 55%, rgba(235,200,117,0) 100%)",
                    }}
                  />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="relative overflow-hidden bg-forest py-20 text-center text-ivory md:py-28">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 50% 30%, rgba(235,200,117,1) 0%, transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <p className="mb-5 text-xs uppercase tracking-widest-2 text-gold md:text-sm">An Invitation</p>
          </Reveal>
          <h2 className="font-display text-3xl font-bold leading-[1.12] tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">
            <WordsReveal text="Vruksha Valley is not just a destination." />
          </h2>
          <Reveal delay={0.3}>
            <p className="mx-auto mt-6 max-w-xl font-serif text-lg italic text-ivory/85 md:mt-8 md:text-2xl">
              It is an invitation to return to nature, to yourself, and to the simple beauty of living in the moment.
            </p>
          </Reveal>
          <Reveal delay={0.5}>
            <Link
              to="/stay"
              data-testid="legacy-stay-cta"
              className="group mt-10 inline-flex items-center gap-2 rounded-full border border-gold/60 bg-transparent px-8 py-4 text-sm font-medium text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-forest md:mt-12"
            >
              Explore Our Stays
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
